import { Eyebrow, RadioCard, RadioCardGroup, Stack } from '@leanin/ui';
import { useTranslations } from 'use-intl';
import type { GroupAccess } from '@leanin/shared/views/event';

// The access-policy radio group used by Circle / Network / Space Visibility
// tabs. Centralizes the wording so a copy tweak doesn't drift between the
// surfaces, and lets the entity label vary (Circle vs Network). Renders bare
// (no Card chrome) so the Visibility tab can compose access + join into a
// single card.

export type AdminAccess = GroupAccess;

type Props = {
  readonly value: AdminAccess;
  readonly onChange: (next: AdminAccess) => void;
  /** "Circle" / "Network" — wordings reference the entity by name. */
  readonly entityLabel: string;
  /** Access options to hide from the picker for this entity. Circles
   *  intentionally omit `unlisted` — leaders shouldn't surface a
   *  reachable-by-link-but-not-listed mode that the rest of the product
   *  copy doesn't explain. `network` is omitted everywhere except a Circle
   *  that actually belongs to a Network, since it has no meaning without a
   *  parent Network to scope discovery to. */
  readonly omit?: ReadonlyArray<AdminAccess>;
};

export function AdminAccessPolicyChoice({ value, onChange, entityLabel, omit }: Props) {
  const t = useTranslations('Group');
  const kind = entityLabel.toLowerCase();
  const allOptions: ReadonlyArray<{ value: AdminAccess; label: string; lede: string }> = [
    {
      value: 'public',
      label: t('admin.access.publicLabel'),
      lede: t('admin.access.publicLede', { kind }),
    },
    {
      value: 'unlisted',
      label: t('admin.access.unlistedLabel'),
      lede: t('admin.access.unlistedLede'),
    },
    {
      value: 'network',
      label: t('admin.access.networkLabel'),
      lede: t('admin.access.networkLede'),
    },
    {
      value: 'private',
      label: t('admin.access.privateLabel'),
      lede: t('admin.access.privateLede', { kind }),
    },
  ];
  const hidden = new Set(omit ?? []);
  const options = allOptions.filter((o) => !hidden.has(o.value));

  return (
    <Stack gap="md">
      <Eyebrow tone="accent">{t('admin.access.eyebrow')}</Eyebrow>
      <RadioCardGroup
        value={value}
        onValueChange={(v) => onChange(v as AdminAccess)}
        aria-label={t('admin.access.ariaLabel', { kind })}
      >
        {options.map((opt) => (
          <RadioCard
            key={opt.value}
            value={opt.value}
            title={opt.label}
            sub={opt.lede}
          />
        ))}
      </RadioCardGroup>
    </Stack>
  );
}
